import React, { useState, useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import htmlParser from "html-react-parser";
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Autoplay, Keyboard, Mousewheel } from 'swiper/modules';
import 'swiper/css';
import "./index.css";

const TestimonialsBlog = ({ data, cdnUrl }) => {
  const [testimonials, setTestimonials] = useState([]);
  const [swiperInstance, setSwiperInstance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const config = data?.testimonialsCarousel || {};

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BLOG_API;
        
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          type: "GET_BLOGS_OF_CATEGORY",
          tag: "testimonials"
        }),
      });
  
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
  
      const result = await response.json();
      
      if (!result || typeof result !== 'object') {
        console.error("Invalid API response - not an object:", result);
        setError("Invalid response format");
        setTestimonials([]);
        setLoading(false);
        return;
      }
  
      let blogsList = [];
      
      if (result.listBlogs && Array.isArray(result.listBlogs)) {
        blogsList = result.listBlogs;
      } else if (result.categoryBlogs && Array.isArray(result.categoryBlogs)) {
        blogsList = result.categoryBlogs;
      } else if (Array.isArray(result)) {
        blogsList = result;
      } else {
        console.error("No valid blog list found in response:", result);
        setError("No blogs found");
        setTestimonials([]);
        setLoading(false);
        return;
      }
      
      const blogs = blogsList.sort((a, b) => new Date(b.date) - new Date(a.date));
      
      setTestimonials(blogs);
      setError(null);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching testimonials:", error);
      setError(error.message);
      setTestimonials([]);
      setLoading(false);
    }
  };
 
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  };

  const extractFirstImage = (content) => {
    if (!content) return null;
    const imgMatch = content.match(/<img[^>]+src="([^">]+)"/);
    return imgMatch ? imgMatch[1] : null;
  };

  const handleCardClick = (testimonial) => {
    const url = `https://www.swmc.com/blog/testimonials${testimonial.url}-${testimonial.id}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Loading state
  if (loading) {
    return (
      <section className="testimonials-blog-section">
        <Container>
          <div className="loading-spinner">Loading testimonials...</div>
        </Container>
      </section>
    );
  }

  // Error state
  if (error) {
    return (
      <section className="testimonials-blog-section">
        <Container>
          <div className="error-message">
            <p>Unable to load testimonials at this time.</p>
            <button onClick={fetchTestimonials} className="retry-btn">
              Retry
            </button>
          </div>
        </Container>
      </section>
    );
  }

  // No testimonials state
  if (testimonials.length === 0) {
    return (
      <section className="testimonials-blog-section">
        <Container>
          <div className="no-content">
            <p>No testimonials available at this time.</p>
          </div>
        </Container>
      </section>
    );
  }

  return (
    <section className="testimonials-blog-section">
      <Container className="testimonials-blog-container">
        <Row className="section-header">
          <Col lg={12} className="text-center">
            <h2 
              className="section-title"
              style={{ color: config.sectionTitle?.color || "#FF5601" }}
            >
              {config.sectionTitle?.text 
                ? htmlParser(config.sectionTitle.text)
                : "Every Home Has a Story Worth Sharing"}
            </h2>
            <p className="section-description">
              {config.sectionDescription 
                ? htmlParser(config.sectionDescription)
                : "Take a closer look at the journey of homeowners who made it happen with Sun West. From first-time buyers to lifelong clients, each story is a reflection of what's possible when care meets expertise."}
            </p>
          </Col>
        </Row>

        <div className="testimonials-carousel-wrapper">
          <Swiper
            modules={[Navigation, Autoplay, Keyboard, Mousewheel]}
            keyboard={{
              enabled: true,
              onlyInViewport: true   
            }}
            mousewheel={{
              enabled: true,
              forceToAxis: true,        
              releaseOnEdges: true,     
              sensitivity: 1           
            }}
            spaceBetween={30}
            slidesPerView={1}
            autoplay={{
              delay: 3000,
              disableOnInteraction: false,
              pauseOnMouseEnter: true
            }}
            speed={800}
            loop={testimonials.length > 3}
            breakpoints={{
              768: {
                slidesPerView: 2,
              },
              992: {
                slidesPerView: 3,
              }
            }}
            onSwiper={setSwiperInstance}
            className="testimonials-swiper-container"
          >
            {testimonials.map((testimonial) => {
              const imageUrl = testimonial.img_name 
                ? `https://d2w24n4g34usfg.cloudfront.net/blog/${testimonial.img_name}`
                : extractFirstImage(testimonial.content);
               
              return (
                <SwiperSlide key={testimonial.id} className="testimonials-swiper-slide">
                  <div
                    className="carousel-card"
                    onClick={() => handleCardClick(testimonial)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="card-inner">
                      <div className="card-image-wrapper">
                        {imageUrl && (
                          <img
                            src={imageUrl}
                            alt={testimonial.title || "Testimonial"}
                            className="card-image"
                            loading="lazy"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              console.error('Image failed to load:', imageUrl);
                            }}
                          />
                        )}
                      </div>
                      
                      <div className="card-content">
                        <p className="card-date">
                          {formatDate(testimonial.date)}
                        </p>
                        <h3 className="card-title">
                          {testimonial.title 
                            ? htmlParser(testimonial.title)
                            : ""}
                        </h3>
                        <span className="read-more-btn">
                          {config.readMoreButton?.text 
                            ? htmlParser(config.readMoreButton.text)
                            : "Read the Full Story"}
                        </span>
                      </div>
                    </div>
                  </div>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>

        <div className="testimonials-navigation-arrows">
          <button 
            className="testimonials-arrow-btn testimonials-arrow-left" 
            onClick={() => swiperInstance?.slidePrev()}
            aria-label="Previous slide"
          >
            <img 
              src={`${cdnUrl}left-arrow.webp`} 
              alt="Previous" 
              className="arrow-image"
            />
          </button>

          <button 
            className="testimonials-arrow-btn testimonials-arrow-right" 
            onClick={() => swiperInstance?.slideNext()}
            aria-label="Next slide"
          >
            <img 
              src={`${cdnUrl}right-arrow.webp`} 
              alt="Next" 
              className="arrow-image"
            />
          </button>
        </div>

        <div className="text-center mt-4">
          <a
            href={config.readMoreButton?.url || "https://www.swmc.com/blog/testimonials/"}
            className="all-blogs-btn"
            target="_blank"
            rel="noopener noreferrer"
          >
            {config.readMoreButton?.text 
              ? htmlParser(config.readMoreButton.text1)
              : "Read All Blogs"}
          </a>
        </div>
      </Container>
    </section>
  );
};

export default TestimonialsBlog;